import React from "react";
import { whereAlpha2, whereAlpha3 } from "iso-3166-1";
import { FormattedHTMLMessage, type InjectedIntl, injectIntl } from "react-intl";
import { type Handle, type OnfidoApi, type SdkParameters } from "onfido-sdk-ui";

import { CustomCssContext } from "@jslib/shared/CustomCssContext";
import { logger } from "@jslib/shared/utils/logger";
import { getSafe } from "@jslib/shared/utils/getSafe";

import { AnnouncingHeader } from "@/components/AnnouncingHeader/AnnouncingHeader";

import {
  type ApplicableDocTypesResponse,
  type Country,
  type FormSelectChoice,
  type IDCheckLoopResponse,
  type VerificationService,
} from "../../../../lib/types/types";
import {
  OnfidoDocumentTypes,
  OnfidoReportNames,
  VerificationStepsEnum,
} from "../../../../lib/types/runtimeTypes";
import { VerificationApiClient } from "../../../../lib/ServerApi/VerificationApiClient";
import { getSortedIdCheckCountryChoices } from "../../../../lib/utils/stepComponentHelpers/stepComponentHelpers";
import { DocTypeCardComponent } from "../../DocTypeCard/DocTypeCard";
import { FormFieldInputSelect } from "../../../FormFields/FormFieldInputSelect/FormFieldInputSelect";

export interface IdCheckLoopOnfidoProps {
  verificationService: VerificationService;
  intl: InjectedIntl;
}

const CONTAINER_ID = "onfido";

const docTypeMap = {
  [OnfidoDocumentTypes.PASSPORT]: {
    title: (
      <FormattedHTMLMessage
        id="step.idCheckLoop.docTypes.passport.title"
        defaultMessage="Passport"
      />
    ),
    subtitle: (
      <FormattedHTMLMessage
        id="step.idCheckLoop.docTypes.passport.subtitle"
        defaultMessage="Photo page"
      />
    ),
    imgSrc: "https://assets-resources.sheerid.com/common/images/2018/icons/government-passport.svg",
  },
  [OnfidoDocumentTypes.DRIVING_LICENCE]: {
    title: (
      <FormattedHTMLMessage
        id="step.idCheckLoop.docTypes.driverLicense.title"
        defaultMessage="Driver's License"
      />
    ),
    subtitle: (
      <FormattedHTMLMessage
        id="step.idCheckLoop.docTypes.driverLicense.subtitle"
        defaultMessage="Front and back"
      />
    ),
    imgSrc: "https://assets-resources.sheerid.com/common/images/2018/icons/drivers-license.svg",
  },
  [OnfidoDocumentTypes.RESIDENCE_PERMIT]: {
    title: (
      <FormattedHTMLMessage
        id="step.idCheckLoop.docTypes.residencePermit.title"
        defaultMessage="Residence Permit"
      />
    ),
    subtitle: (
      <FormattedHTMLMessage
        id="step.idCheckLoop.docTypes.residencePermit.subtitle"
        defaultMessage="Front and back"
      />
    ),
    imgSrc: "https://assets-resources.sheerid.com/common/images/2018/icons/resident-permit.svg",
  },
  [OnfidoDocumentTypes.NATIONAL_IDENTITY_CARD]: {
    title: (
      <FormattedHTMLMessage
        id="step.idCheckLoop.docTypes.identityCard.title"
        defaultMessage="Identity Card"
      />
    ),
    subtitle: (
      <FormattedHTMLMessage
        id="step.idCheckLoop.docTypes.identityCard.subtitle"
        defaultMessage="Front and back"
      />
    ),
    imgSrc: "https://assets-resources.sheerid.com/common/images/2018/icons/government-id.svg",
  },
};

const IdCheckLoopOnfido = ({ verificationService, intl }: IdCheckLoopOnfidoProps) => {
  const verificationResponse = verificationService.verificationResponse as IDCheckLoopResponse;
  const { formButtonColor, formButtonFontColor, formLinkColor, formPrimaryFontColor } =
    React.useContext(CustomCssContext);

  const initialCountrySelection: FormSelectChoice<Country, string> =
    verificationResponse.country === undefined
      ? undefined
      : {
          value: verificationResponse.country,
          label: intl.formatHTMLMessage({
            id: `countries.${verificationResponse.country}`,
            defaultMessage: verificationResponse.country,
          }),
        };

  const [countrySelection, setCountrySelection] = React.useState(initialCountrySelection);
  const [docTypeSelection, setDocTypeSelection] = React.useState("");
  const [docTypeOptions, setDocTypeOptions] = React.useState([]);

  const onfidoRef = React.useRef<Handle | null>(null);
  const isInitializing = React.useRef(false);

  const countryList: Country[] = getSafe(
    () =>
      verificationService.programTheme.config.onfidoIncludedCountries.map(
        (country) => whereAlpha3(country).alpha2 as Country,
      ),
    [],
  );

  const countryChoices: FormSelectChoice<Country, string>[] = getSortedIdCheckCountryChoices(
    countryList,
    intl,
    verificationResponse.locale,
  );

  const submitIdCheckLoop = (data) => {
    logger.info("StepIDCheckLoop submitting ID Check Loop", data);
    verificationService.submitStep(
      VerificationStepsEnum.idCheckLoop,
      verificationService.viewModel,
      verificationService.verificationResponse,
    );
  };

  const initializeOnfido = async (country: string, documentType: string) => {
    if (isInitializing.current) {
      return;
    }
    isInitializing.current = true;

    if (onfidoRef.current) {
      onfidoRef.current.tearDown();
      onfidoRef.current = null;
    }

    try {
      const onfidoStepConfig = getSafe(
        () => verificationService.programTheme.config.onfidoReportNames,
        [OnfidoReportNames.DOCUMENT, OnfidoReportNames.FACIAL_SIMILARITY_MOTION],
      ).map((reportName) => {
        if (reportName === OnfidoReportNames.DOCUMENT) {
          return {
            type: "document",
            options: { forceCrossDevice: true, documentTypes: { [documentType]: { country } } },
          };
        }
        if (reportName === OnfidoReportNames.FACIAL_SIMILARITY_MOTION) {
          return {
            type: "face",
            options: { requestedVariant: "motion", motionFallbackVariant: "video" },
          };
        }
        return reportName;
      });

      const OnfidoModule = await import("onfido-sdk-ui");

      onfidoRef.current = (OnfidoModule.Onfido as OnfidoApi).init({
        containerId: CONTAINER_ID,
        token: verificationResponse.sdkToken,
        onComplete: (data) => {
          if (Object.keys(data).length !== 0) {
            submitIdCheckLoop(data);
          }
        },
        steps: [...onfidoStepConfig, "complete"],
        // Documenation -- https://documentation.onfido.com/sdk/web/#language-selection
        language: verificationService.viewModel.localeChoice.value.replace("-", "_"),
        // Documenation -- https://documentation.onfido.com/sdk/web/#styling-customization
        theme: {
          name: "light",
          config: {
            borderRadiusButton: "0px",
            borderStyleSurfaceModal: "none",
            colorBackgroundSurfaceModal: "transparent",
            colorContentBody: formPrimaryFontColor,
            colorContentButtonPrimaryText: formButtonFontColor,
            colorBackgroundButtonPrimary: formButtonColor,
            colorBackgroundButtonPrimaryHover: formButtonColor,
            colorBorderButtonPrimary: "transparent",
            colorBorderButtonPrimaryHover: "transparent",
            colorBackgroundButtonPrimaryActive: formButtonColor,
            colorIcon: formButtonColor,
            colorInputOutline: "transparent",
            colorContentButtonTertiaryText: formLinkColor,
          },
        },
      } as SdkParameters);
    } catch (error) {
      logger.error("Onfido initialization failed", error);
    } finally {
      isInitializing.current = false;
    }
  };

  React.useEffect(() => {
    const countryCodeAlpha3 = getSafe(() => whereAlpha2(countrySelection.value).alpha3);
    if (countryCodeAlpha3 !== undefined) {
      VerificationApiClient.getIdCheckDocTypeForCountry(verificationResponse.verificationId, [
        countryCodeAlpha3,
      ]).then((value: ApplicableDocTypesResponse) =>
        setDocTypeOptions(Object.values(value.results)[0]),
      );
    } else {
      setDocTypeOptions([]);
    }
  }, [countrySelection]);

  React.useEffect(() => {
    if (countrySelection !== undefined && docTypeSelection !== "") {
      const countryAlpha3 = getSafe(() => whereAlpha2(countrySelection.value).alpha3);
      initializeOnfido(countryAlpha3, docTypeSelection);
    }

    return () => {
      if (onfidoRef.current) {
        onfidoRef.current.tearDown();
        onfidoRef.current = null;
      }
    };
  }, [countrySelection, docTypeSelection]);

  return (
    <div>
      {countrySelection !== undefined && docTypeSelection !== "" && <div id={CONTAINER_ID} />}
      {docTypeSelection === "" && (
        <div>
          <AnnouncingHeader align="center">
            <FormattedHTMLMessage
              id="step.idCheckLoop.docTypeSelector.title"
              defaultMessage="Choose your document"
            />
          </AnnouncingHeader>
          <div className="sid-header__subtitle sid-l-horz-center sid-l-space-btm-lg">
            <FormattedHTMLMessage
              id="step.idCheckLoop.docTypeSelector.subtitle"
              defaultMessage="You'll need access to a mobile phone with a camera or computer with a webcam."
            />
          </div>

          <div className="sid-identity-check-doc-types">
            <FormFieldInputSelect
              fieldId="issuingCountry"
              label={
                <FormattedHTMLMessage
                  id="step.idCheckLoop.countrySelector.title"
                  defaultMessage="Issuing Country"
                />
              }
              options={countryChoices}
              value={countrySelection}
              onChange={async (countryChoice: FormSelectChoice<Country, string>) => {
                setCountrySelection(countryChoice);
              }}
              isRequired={false}
            />

            {docTypeOptions.map((docTypeOption) => {
              if (Object.prototype.hasOwnProperty.call(docTypeMap, docTypeOption.documentType)) {
                const config = docTypeMap[docTypeOption.documentType];
                return (
                  <DocTypeCardComponent
                    icon={
                      <img
                        className="sid-identity-check-doc-type__icon"
                        alt=""
                        src={config.imgSrc}
                      />
                    }
                    title={config.title}
                    subtitle={config.subtitle}
                    setDocType={() => setDocTypeSelection(docTypeOption.documentType)}
                    key={docTypeOption.documentType}
                  />
                );
              }
              return null;
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export const IdCheckLoopOnfidoComponent = injectIntl(IdCheckLoopOnfido);
